import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';

import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { FormatDatasetAdapter } from 'app/model/adapters/datasets/format-dataset.adapter';
import { HierarchicalNode } from 'app/model/dataset/hierarchicalnode';
import { AvroDataset, compressionCodecType } from 'shell-models-lib';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { Column } from 'app/model/dataset/column';
import { NamedArray } from 'app/model/base/namedarray';
import { avroPhysicalToLogicalMapping } from 'app/shared/utils/column-type.util';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AvroDataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            folder: '*',
            annotations: '*',
            schema: '*',
            parameters: '*',
            type: '*',
            linkedServiceName: '*',
            linkedService: '*',
            connectionSettings: __IS_FABRIC_DPE__ ? '*' : undefined,
            connectionProperties: GlobalVariables.disableServerCertificate ? '*' : undefined,
            typeProperties: {
                location: '*',
                avroCompressionCodec: '*',
                avroCompressionLevel: '*'
            }
        }
    }
})
export class AvroDatasetAdapter extends FormatDatasetAdapter<AvroDataset, Model.DatasetV2Resource> {
    public parseHierarchicalSchema(obj: unknown): HierarchicalNode {
        try {
            return parseAvroSchema(obj);
        } catch {
            return undefined;
        }
    }

    protected async _toResource(dataset: AvroDataset): Promise<Model.DatasetV2Resource> {
        const modelDatasetResource = await super._toResource(dataset) as Model.DatasetV2Resource;
        const modelDataset = this.properties(modelDatasetResource) as Model.AvroDataset;
        modelDataset.type = Metamodel.modelFor(AvroDataset).name;
        modelDataset.typeProperties = modelDataset.typeProperties || {};
        if (dataset.location) {
            modelDataset.typeProperties.location = this.locationToResource(dataset);
        }

        modelDataset.typeProperties.avroCompressionCodec = ValueAdapter.toResource(dataset, AvroDataset.COMPRESSION_CODEC);
        if (modelDataset.typeProperties.avroCompressionCodec != null &&
            (modelDataset.typeProperties.avroCompressionCodec === compressionCodecType.deflate || !stringIsNullOrEmpty(AvroDataset.COMPRESSION_CODEC.templateValue(dataset)) != null)) {
            modelDataset.typeProperties.avroCompressionLevel = ValueAdapter.toResource(dataset, AvroDataset.COMPRESSION_LEVEL);
        }

        modelDataset.schema = dataset.rawHierarchicalSchema ? dataset.rawHierarchicalSchema : {};

        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }
        return modelDatasetResource;
    }

    protected async _toModel(modelDatasetResource: Model.DatasetV2Resource, dataset?: AvroDataset): Promise<AvroDataset> {
        if (!dataset) {
            dataset = AvroDataset.MODEL.create<AvroDataset>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);
        if (modelDatasetResource
            && modelDatasetResource.properties
            && modelDatasetResource.properties.typeProperties
            && modelDatasetResource.properties.typeProperties.location) {
            await this.locationToModel(modelDatasetResource, dataset);
        }

        const props = modelDatasetResource.properties as Model.AvroDataset;

        if (props.typeProperties != null) {
            ValueAdapter.toModel(dataset, AvroDataset.COMPRESSION_CODEC, props.typeProperties.avroCompressionCodec);
            ValueAdapter.toModel(dataset, AvroDataset.COMPRESSION_LEVEL, props.typeProperties.avroCompressionLevel);
        }

        dataset.rawHierarchicalSchema = modelDatasetResource.properties.schema;
        if (dataset.rawHierarchicalSchema != null) {
            dataset.schemas = this.parseHierarchicalSchema(dataset.rawHierarchicalSchema);
            dataset.columns = dataset.schemas.children.map((c) => {
                const col = new Column();
                col.name = c.name;
                col.physicalType = c.columnType;
                col.columnType = c.columnType;
                col.logicalType = avroPhysicalToLogicalMapping[(c.columnType || '').toLowerCase()];
                return col;
            }) as NamedArray<Column>;
        }
        dataset.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelDatasetResource, AvroDataset.MODEL));
        return dataset;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseAvroSchema(schema: any, name?: string): HierarchicalNode {
    const root = new HierarchicalNode();
    if (schema != null && Object.keys(schema).length > 0) {
        root.name = schema.name;
        root.columnType = 'record';
        root.kind = 'object';

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const parseNode = (curNode: HierarchicalNode, obj: any): void => {
            curNode.name = obj.name;

            // eslint-disable-next-line
            if (typeof obj.type === 'string') {
                // the type is primitive type
                curNode.columnType = obj.type;
            } else if (Array.isArray(obj.type)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const typeObj = (obj.type as any[]).find((t) => typeof t === 'object');
                // parse union of object, can only contain at most 1 object
                if (typeObj != null) {
                    curNode.columnType = typeObj.type;
                    obj = typeObj;
                } else {
                    curNode.columnType = (obj.type as []).map((t) => typeof t === 'string' ? t : 'any').join(' | ');
                }
            } else {
                // the type is complex type
                curNode.columnType = obj.type.type;
                obj = obj.type;
            }

            if (curNode.columnType === 'array') {
                curNode.items = new HierarchicalNode();
                curNode.kind = 'array';
                if (typeof obj.items === 'string') {
                    curNode.items.columnType = obj.items;
                } else if (Array.isArray(obj.items)) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const typeObj = (obj.items as any[]).find((t) => typeof t === 'object');
                    // parse union of object, can only contain at most 1 object
                    if (typeObj != null) {
                        parseNode(curNode.items, typeObj);
                    } else {
                        curNode.items.columnType = (obj.items as []).find((t) => !/null/i.test(t));
                    }
                } else {
                    parseNode(curNode.items, obj.items);
                }
            } else if (curNode.columnType === 'record') {
                curNode.kind = 'object';
                curNode.properties = {};
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (obj.fields as any[]).forEach((prop) => {
                    const child = new HierarchicalNode();
                    curNode.properties[prop.name] = child;
                    parseNode(child, prop);
                });
            }
        };
        parseNode(root, schema);
    }
    return root;
}
